import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Slide } from '@mui/material';
import { Icon } from '@iconify/react';
import { updateOccupant, getOccupantDetails } from '../../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import SearchableDropdown from '../../../../../components/SearchableDropdown';
import MemberRoleSelect from '../../../../../components/MemberRoleSelect';
import usePermissions from '../../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../../consts/permissions';
import { selectedSocietyStorer } from '../../../../../utils/webStorer';
import fetchCurrentRoleId from '../../../../../utils/helpers/fetchCurrentRole';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const RELATION_OPTIONS = [
    { value: 'SPOUSE', label: 'Spouse' },
    { value: 'CHILD', label: 'Child' },
    { value: 'PARENT', label: 'Parent' },
    { value: 'SIBLING', label: 'Sibling' },
    { value: 'OTHER', label: 'Other' },
];

const EditFamilyModal = ({
    open,
    handleClose,
    member,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const { updateLoading, occupantDetails, detailsLoading } = useSelector(state => state.OccupantReducer);
    const { can } = usePermissions();
    // Anyone who may add / edit members picks the login role; the options
    // (and the API) limit non-admins to member roles.
    const canPickRole = can(PERMISSIONS.USER_MANAGE_ROLES) || can(PERMISSIONS.OCCUPANT_UPDATE);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        relation: '',
        role_id: '',
        is_resident: false,
    });
    const [errors, setErrors] = useState({});

    // Fetch occupant details when modal opens
    useEffect(() => {
        if (member && open) {
            dispatch(getOccupantDetails({ id: member.occupant_id, type: 'family' }));
        }
        setErrors({});
    }, [member, open, dispatch]);

    // Populate form when details are fetched
    useEffect(() => {
        if (occupantDetails && open) {
            const details = occupantDetails;
            setFormData((prev) => ({
                ...prev,
                first_name: details.first_name || '',
                last_name: details.last_name || '',
                email: details.email || '',
                phone: details.phone || '',
                relation: details.relation || '',
                is_resident: !!details.is_resident,
            }));
        }
    }, [occupantDetails, open]);

    // Preselect the family member's current login role from user-societies.
    useEffect(() => {
        if (!open || !canPickRole || !member) return;
        const societyId = selectedSocietyStorer.getSocietyId();
        let cancelled = false;
        fetchCurrentRoleId({
            societyId,
            sourceType: 'OCCUPANCY',
            sourceRowId: member.occupant_id,
        }).then((roleId) => {
            if (!cancelled && roleId) {
                setFormData((prev) => ({ ...prev, role_id: roleId }));
            }
        });
        return () => { cancelled = true; };
    }, [open, canPickRole, member]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRelationChange = (value) => {
        setFormData(prev => ({ ...prev, relation: value }));
        if (errors.relation) {
            setErrors(prev => ({ ...prev, relation: '' }));
        }
    };

    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role_id: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.first_name.trim()) newErrors.first_name = 'First Name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last Name is required';
        // Phone is optional for family members — children and dependents often
        // have none — matching AddFamily and occupantValidator, which both
        // allow it to be blank. If given, still enforce the 10-digit format.
        if (formData.phone && formData.phone.trim() && !/^\d{10}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Phone must be exactly 10 digits';
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.relation) newErrors.relation = 'Relation is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = {
            id: member.occupant_id,
            type: 'FAMILY',
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email || undefined,
            // Send '' rather than dropping the key: clearing an existing phone
            // has to reach the server, and occupantValidator allows the empty
            // string. Trimmed so stray whitespace isn't stored as a number.
            phone: formData.phone ? formData.phone.trim() : '',
            relation: formData.relation,
            // Joi.boolean() on the update schema too — a real boolean, not 1/0.
            is_resident: !!formData.is_resident,
        };
        if (canPickRole && formData.role_id) payload.role_id = Number(formData.role_id);

        payload.onSuccess = () => {
            handleClose();
            if (onSuccess) onSuccess();
        };

        dispatch(updateOccupant(payload));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'visible', minHeight: '420px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
            <div className="tw-bg-white tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
                {/* Compact Header */}
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-blue-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:account-edit-outline" className="tw-text-blue-600 tw-text-base" />
                        </div>
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800">Edit Family Member</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="tw-w-7 tw-h-7 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-transition-colors"
                    >
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                {/* Loading State */}
                {detailsLoading ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-blue-600 tw-border-t-transparent"></div>
                        <p className="tw-text-sm tw-text-gray-500 tw-mt-3">Loading details...</p>
                    </div>
                ) : (
                    /* Form */
                    <form onSubmit={onSubmit} className="tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
                        <div className="tw-px-5 tw-py-5 tw-space-y-5 tw-overflow-visible tw-flex-1">
                            {/* Personal Info */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                                    <Icon icon="mdi:account-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Personal Information</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">
                                            First Name <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            className={`tw-w-full tw-px-3 tw-py-2.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.first_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="First Name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            maxLength={50}
                                        />
                                        {errors.first_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-1">{errors.first_name}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">
                                            Last Name <span className="tw-text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            className={`tw-w-full tw-px-3 tw-py-2.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.last_name ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Last Name"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            maxLength={50}
                                        />
                                        {errors.last_name && <p className="tw-text-red-500 tw-text-[11px] tw-mt-1">{errors.last_name}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                                    <Icon icon="mdi:phone-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Contact Details</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">
                                            Phone <span className="tw-text-gray-400 tw-font-normal">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="phone"
                                            className={`tw-w-full tw-px-3 tw-py-2.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.phone ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Phone Number"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            maxLength={15}
                                        />
                                        {errors.phone && <p className="tw-text-red-500 tw-text-[11px] tw-mt-1">{errors.phone}</p>}
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">Residing in Flat</label>
                                        <label className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2.5 tw-border tw-border-gray-200 tw-rounded-lg tw-cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="is_resident"
                                                checked={formData.is_resident}
                                                onChange={(e) => setFormData(prev => ({ ...prev, is_resident: e.target.checked }))}
                                            />
                                            <span className={`tw-text-sm tw-font-medium ${formData.is_resident ? 'tw-text-emerald-600' : 'tw-text-gray-500'}`}>
                                                {formData.is_resident ? 'Residing' : 'Non-Residing'}
                                            </span>
                                        </label>
                                    </div>
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className={`tw-w-full tw-px-3 tw-py-2.5 tw-border tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 ${errors.email ? 'tw-border-red-400' : 'tw-border-gray-200'}`}
                                            placeholder="Email Address"
                                            value={formData.email}
                                            onChange={handleChange}
                                            maxLength={100}
                                        />
                                        {errors.email && <p className="tw-text-red-500 tw-text-[11px] tw-mt-1">{errors.email}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Relationship */}
                            <div>
                                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                                    <Icon icon="mdi:heart-outline" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wide">Relationship</span>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <div>
                                        <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">
                                            Relation <span className="tw-text-red-500">*</span>
                                        </label>
                                        <SearchableDropdown
                                            value={formData.relation}
                                            onChange={handleRelationChange}
                                            options={RELATION_OPTIONS}
                                            valueKey="value"
                                            labelKey="label"
                                            placeholder="Select Relation"
                                            className="tw-w-full"
                                        />
                                        {errors.relation && <p className="tw-text-red-500 tw-text-[11px] tw-mt-1">{errors.relation}</p>}
                                    </div>
                                    {canPickRole && (
                                        <div>
                                            <label className="tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1.5 tw-block">Login Role</label>
                                            <MemberRoleSelect
                                                value={formData.role_id}
                                                onChange={handleRoleChange}
                                                placeholder="Default: Resident"
                                                className="tw-w-full"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="tw-px-5 tw-py-3 tw-bg-gray-50 tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={updateLoading}
                                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={updateLoading}
                                className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50"
                            >
                                {updateLoading ? (
                                    <>
                                        <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-white tw-border-t-transparent"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="mdi:check" className="tw-text-base" />
                                        Update Family
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Dialog>
    );
};

export default EditFamilyModal;
